import { useState } from 'react';
import { Star, ChartNoAxesCombined, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useLabPreferences } from './preferences';

import { Button } from '@/components/ui/button';
import { type LabResult, useMaternalLabs } from './api';
import { usePermissions } from '@/features/permissions';
import { UploadedDocumentInboxSheet } from '@/features/emr';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export function groupLabs(results: LabResult[]) {
  const dates = [...new Set(results.map((r) => r.collected_date ?? r.reported_date ?? 'Undated'))].sort((a, b) =>
    a === 'Undated' ? 1 : b === 'Undated' ? -1 : b.localeCompare(a),
  );
  const groups = new Map<string, LabResult[]>();
  results.forEach((r) => groups.set(r.row_key, [...(groups.get(r.row_key) ?? []), r]));
  return { dates, groups: [...groups.entries()].sort((a, b) => a[1][0].test_name.localeCompare(b[1][0].test_name)) };
}

function LabRange({ results }: { results: LabResult[] }) {
  const ranges = [...new Set(results.map((r) => r.reference_range.trim()).filter(Boolean))];
  return (
    <div className="flex min-h-8 flex-col justify-center">
      <span>{ranges.length ? ranges.join(' / ') : '—'}</span>
      {ranges.length > 1 && <small className="text-muted-foreground">Varies by result · see details</small>}
    </div>
  );
}

export function MaternalLabs({ patientId, pregnancyId = '' }: { pregnancyId?: string; patientId?: string }) {
  const { can } = usePermissions();
  const preferences = useLabPreferences();
  const query = useMaternalLabs(pregnancyId, can('read_patient'), patientId);
  const [source, setSource] = useState<{ documentId: string; revisionId?: string; pageNumber: number }>();
  if (!can('read_patient')) return null;
  const { dates, groups } = groupLabs(query.data?.results ?? []);
  const followed = groups.filter(([key]) => preferences.followed.includes(key));
  const others = groups.filter(([key]) => !preferences.followed.includes(key));
  const matrix = (rows: typeof groups) => (
    <div className="max-h-[36rem] overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Test / units</TableHead>
            <TableHead>Range</TableHead>
            {dates.map((date) => (
              <TableHead key={date}>{date}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(([key, results]) => (
            <TableRow key={key}>
              <TableCell className="min-w-52 py-1 align-top">
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 shrink-0"
                    aria-label={`Follow ${results[0].test_name}`}
                    aria-pressed={preferences.followed.includes(key)}
                    onClick={() => preferences.toggle('followed', key)}>
                    <Star fill={preferences.followed.includes(key) ? 'currentColor' : 'none'} />
                  </Button>
                  <Button
                    variant={preferences.graphed.includes(key) ? 'secondary' : 'ghost'}
                    size="icon"
                    className="size-8 shrink-0"
                    aria-label={`Graph ${results[0].test_name}`}
                    aria-pressed={preferences.graphed.includes(key)}
                    onClick={() => preferences.toggle('graphed', key)}>
                    <ChartNoAxesCombined />
                  </Button>
                  <span>
                    <strong>{results[0].test_name}</strong>
                    {results[0].unit && ` (${results[0].unit})`}
                  </span>
                </div>
              </TableCell>
              <TableCell className="min-w-28 py-1 align-top">
                <LabRange results={results} />
              </TableCell>
              {dates.map((date) => {
                const values = results.filter((r) => (r.collected_date ?? r.reported_date ?? 'Undated') === date);
                return (
                  <TableCell key={date} className="min-w-32 py-1 align-top">
                    {values.map((r) => (
                      <div key={r.id} className="flex h-8 items-center gap-1 whitespace-nowrap">
                        <span
                          className={r.flag && !['N', 'NEG'].includes(r.flag) ? 'font-semibold text-destructive' : ''}>
                          {r.value} {r.unit}
                          {r.flag && ` · ${r.flag}`}
                        </span>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="size-7"
                              aria-label={`Details for ${r.test_name} on ${date}`}>
                              <ChevronDown />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="flex flex-col gap-2 text-sm">
                            <strong>{r.test_name}</strong>
                            <span>
                              {r.value} {r.unit}
                              {r.flag && ` · ${r.flag}`}
                            </span>
                            <span>Range: {r.reference_range || 'Not provided'}</span>
                            <span>
                              {date}
                              {!r.collected_date && r.reported_date && ' · Reported'}
                            </span>
                            {r.specimen && <span>Specimen: {r.specimen}</span>}
                            {r.method && <span>Method: {r.method}</span>}
                            {r.source_system === 'referral_intake' && (
                              <span>Extracted from referral · patient record</span>
                            )}
                            <span>Status: {r.status}</span>
                            {r.accession && <span>Sample: {r.accession}</span>}
                            {r.source_system && <span>Imported record</span>}
                            {r.document_id && can('read_document') && (
                              <Button
                                size="sm"
                                variant="link"
                                onClick={() =>
                                  setSource({
                                    pageNumber: r.page,
                                    revisionId: r.revision_id,
                                    documentId: r.document_id!,
                                  })
                                }>
                                Source · page {r.page}
                              </Button>
                            )}
                          </PopoverContent>
                        </Popover>
                      </div>
                    ))}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
  return (
    <section aria-label="Maternal laboratory flowsheet" className="flex min-w-0 flex-col gap-3 rounded-md border p-3">
      <h3 className="font-medium">Maternal Flowsheet · Laboratory results</h3>
      <p className="text-sm text-muted-foreground">
        Columns use collection date when available. “Reported” indicates a report date was used. Flags are copied from
        the laboratory; blank does not mean normal.
      </p>
      {query.isLoading && <p>Loading results…</p>}
      {query.isError && (
        <p role="alert">
          Unable to load results.{' '}
          <Button variant="link" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </p>
      )}
      {!query.isLoading && !query.isError && !groups.length && (
        <p>No laboratory results yet. Save referral intake or import results from a fax.</p>
      )}
      {!!groups.length && (
        <>
          <p className="text-sm text-muted-foreground">
            Star tests to follow them here. Select graph icons to include tests in Value Trends. Preferences are saved
            in this browser.
          </p>
          {!!followed.length && (
            <section aria-label="Followed labs">
              <h4>Followed labs</h4>
              {matrix(followed)}
            </section>
          )}
          {!followed.length && <p>No followed tests yet. Open All labs to choose tests.</p>}
          <Collapsible>
            <CollapsibleTrigger asChild>
              <Button variant="ghost">
                <ChevronDown />
                All labs ({others.length})
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>{matrix(others)}</CollapsibleContent>
          </Collapsible>
        </>
      )}
      <UploadedDocumentInboxSheet
        open={!!source}
        documentId={source?.documentId}
        pageNumber={source?.pageNumber}
        revisionId={source?.revisionId}
        onOpenChange={(open) => {
          if (!open) setSource(undefined);
        }}
      />
    </section>
  );
}
